import {
  StockCommandV1Schema,
  type StockBalanceExpectationV1,
  type StockCommandV1,
  type StockCorrectionSourceV1,
} from './stockCommand.js';

type ConsumeCommand = Extract<StockCommandV1, { movement_class: 'consume' }>;
type InventoryAdjustmentCommand = Extract<
  StockCommandV1,
  { movement_class: 'inventory_adjustment' }
>;
type CorrectionCommand = Extract<StockCommandV1, { movement_class: 'correction' }>;

export interface StockCommandLineProvenanceV1 {
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
  /**
   * Immutable human-readable source reference for a non-commercial stock
   * fact, for example an owner-attested inventory count. It is never a DDT
   * identifier unless `documentId` is also provided by the caller.
   */
  lineRef?: string | null;
}

export interface StockCorrectionConversionConfirmationV1 {
  factor: number;
  referenceId: string;
}

export function buildConsumeStockCommandV1(input: {
  commandId: string;
  tenantId: string;
  businessId: string;
  foodId: string;
  lot: string | null;
  expirationDate: string | null;
  qty: number;
  uom: string;
  reason: string;
  closeLot?: boolean;
  operatorName: string | null;
  provenance: StockCommandLineProvenanceV1;
}): ConsumeCommand {
  return StockCommandV1Schema.parse({
    version: 1,
    command_id: input.commandId,
    tenant_id: input.tenantId,
    movement_class: 'consume',
    economic_effect: 'stock_only',
    reason: input.reason,
    business_id: input.businessId,
    lines: [{
      food_id: input.foodId,
      batch_lot: input.lot,
      expiration_date: input.expirationDate,
      supplier_id: input.provenance.supplierId,
      document_id: input.provenance.documentId,
      storage_unit_id: input.provenance.storageUnitId,
      line_ref: input.provenance.lineRef ?? null,
      operator_name: input.operatorName?.trim() || null,
      source_qty: input.qty,
      source_uom: input.uom,
      source_unit_price: null,
      source_line_total: null,
      currency: 'EUR',
      stock_uom: input.uom,
      conversion: { factor: 1, source: 'identity' },
      ...(input.closeLot ? { close_lot: true as const } : {}),
    }],
  }) as ConsumeCommand;
}

export function buildInventoryAdjustmentStockCommandV1(input: {
  commandId: string;
  tenantId: string;
  businessId: string;
  foodId: string;
  lot: string | null;
  expirationDate: string | null;
  systemQty: number;
  countedQty: number;
  uom: string;
  countedAt: string;
  reason: string;
  operatorName: string | null;
  unitCost: number | null;
  currency: string;
  provenance: StockCommandLineProvenanceV1;
}): InventoryAdjustmentCommand {
  return StockCommandV1Schema.parse({
    version: 1,
    command_id: input.commandId,
    tenant_id: input.tenantId,
    movement_class: 'inventory_adjustment',
    economic_effect: 'stock_only',
    reason: input.reason,
    business_id: input.businessId,
    counted_at: input.countedAt,
    lines: [{
      food_id: input.foodId,
      batch_lot: input.lot,
      expiration_date: input.expirationDate,
      supplier_id: input.provenance.supplierId,
      document_id: input.provenance.documentId,
      storage_unit_id: input.provenance.storageUnitId,
      line_ref: input.provenance.lineRef ?? null,
      operator_name: input.operatorName?.trim() || null,
      system_qty: input.systemQty,
      counted_qty: input.countedQty,
      uom: input.uom,
      unit_cost: input.unitCost,
      currency: input.currency,
    }],
  }) as InventoryAdjustmentCommand;
}

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

export function stockCorrectionConversionV1(
  sourceQty: number,
  sourceUom: string,
  targetQty: number,
  targetUom: string,
  confirmation?: StockCorrectionConversionConfirmationV1,
): {
  factor: number;
  source: 'identity' | 'metric' | 'user_confirmed';
  reference_id?: string;
} {
  const factor = targetQty / sourceQty;
  const from = normalizedUom(sourceUom);
  const to = normalizedUom(targetUom);
  if (from === to && Math.abs(factor - 1) <= Number.EPSILON * 16) {
    return { factor: 1, source: 'identity' };
  }
  const metricUnits: Readonly<Record<string, number>> = {
    kg: 1, g: 0.001, l: 1, ml: 0.001,
  };
  const sameMetricFamily = (
    (from === 'kg' || from === 'g') && (to === 'kg' || to === 'g')
  ) || (
    (from === 'l' || from === 'ml') && (to === 'l' || to === 'ml')
  );
  const metric = sameMetricFamily ? metricUnits[from] / metricUnits[to] : null;
  if (metric !== null && Math.abs(factor - metric) <= 1e-12 * Math.max(1, factor, metric)) {
    return { factor, source: 'metric' };
  }
  const referenceId = confirmation?.referenceId.trim();
  if (
    !confirmation
    || !Number.isFinite(confirmation.factor)
    || confirmation.factor <= 0
    || !referenceId
    || Math.abs((sourceQty * confirmation.factor) - targetQty)
      > 1e-12 * Math.max(1, Math.abs(targetQty))
  ) {
    throw new Error(
      `La conversione ${sourceUom}→${targetUom} richiede un fattore confermato`,
    );
  }
  return {
    factor: confirmation.factor,
    source: 'user_confirmed',
    reference_id: referenceId,
  };
}

export function buildStockCorrectionCommandV1(input: {
  commandId: string;
  tenantId: string;
  reason: string;
  lineRef: string;
  operatorName: string;
  documentNumber: string | null;
  source: Omit<StockCorrectionSourceV1, 'expected_balance'> & {
    expectedBalance: StockBalanceExpectationV1;
  };
  target: { batchLot: string | null; expirationDate: string | null; qty: number; uom: string };
  conversionConfirmation?: StockCorrectionConversionConfirmationV1;
}): CorrectionCommand {
  const conversion = stockCorrectionConversionV1(
    input.source.qty,
    input.source.uom,
    input.target.qty,
    input.target.uom,
    input.conversionConfirmation,
  );
  return StockCommandV1Schema.parse({
    version: 1,
    command_id: input.commandId,
    tenant_id: input.tenantId,
    movement_class: 'correction',
    economic_effect: 'stock_only',
    reason: input.reason,
    lines: [{
      line_ref: input.lineRef,
      operator_name: input.operatorName,
      document_number: input.documentNumber,
      source: {
        ...input.source,
        expected_balance: input.source.expectedBalance,
      },
      target: {
        batch_lot: input.target.batchLot,
        expiration_date: input.target.expirationDate,
        qty: input.target.qty,
        uom: input.target.uom,
      },
      conversion,
    }],
  }) as CorrectionCommand;
}
